<template>
  <div class="w-screen h-screen lg:p-10 bg-pure-white overflow-hidden">
    <div class="bl-auth-background px-4 lg:p-0">
      <div class="bl-auth-card rounded-3xl bg-pure-white p-6 lg:p-14">
        <div class="space-y-3 mb-6 lg:w-160">
          <BlTextTitle4 text="Change your password" />
          <BlTextBody2
            text="Please enter your new password to keep your personal information safe."
          />
        </div>

        <div
          v-if="verifiedTokenLoading"
          class="lg:w-160 h-64 grid place-items-center"
        >
          <div class="blum-loader"></div>
        </div>
        <form v-else @submit.prevent="savePassword">
          <BlInputText
            class="mb-4"
            :input="passwordInput"
            @changed="inputHandler"
          />

          <div class="grid gap-2 lg:grid-cols-3 mb-2 lg:mb-4">
            <div class="flex items-center gap-2">
              <BlIcon
                :icon="
                  passwordInputChecked && !passwordHasSixChar
                    ? 'close'
                    : 'check'
                "
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="
                  passwordHasSixChar
                    ? 'bg-success-400'
                    : passwordInputChecked
                    ? 'bg-error-300 p-1'
                    : 'bg-grey-300'
                "
              />
              <div class="text-xs">6 characters minimum</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                :icon="
                  passwordInputChecked && !passwordHasUppercase
                    ? 'close'
                    : 'check'
                "
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="
                  passwordHasUppercase
                    ? 'bg-success-400'
                    : passwordInputChecked
                    ? 'bg-error-300 p-1'
                    : 'bg-grey-300'
                "
              />
              <div class="text-xs">1 uppercase character</div>
            </div>
          </div>
          <div class="grid gap-2 lg:grid-cols-3">
            <div class="flex items-center gap-2">
              <BlIcon
                :icon="
                  passwordInputChecked && !passwordHasLowescase
                    ? 'close'
                    : 'check'
                "
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="
                  passwordHasLowescase
                    ? 'bg-success-400'
                    : passwordInputChecked
                    ? 'bg-error-300 p-1'
                    : 'bg-grey-300'
                "
              />
              <div class="text-xs">1 lowercase character</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                :icon="
                  passwordInputChecked && !passwordHasNumber ? 'close' : 'check'
                "
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="
                  passwordHasNumber
                    ? 'bg-success-400'
                    : passwordInputChecked
                    ? 'bg-error-300 p-1'
                    : 'bg-grey-300'
                "
              />
              <div class="text-xs">1 number</div>
            </div>
          </div>

          <div class="flex items-center justify-end gap-4">
            <BlInputButton type="plain" label="Cancel" @clicked="goSignin" />
            <BlInputButton
              :disabled="!passwordInput.model || !verifiedToken"
              buttonType="submit"
              label="Update"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputText from "../BlInputText.vue";
import BlInputButton from "../BlInputButton.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "ResetPassword",
  components: { BlTextBody2, BlInputText, BlInputButton, BlTextTitle4, BlIcon },
  data() {
    return {
      verifiedTokenLoading: true,
      verifiedToken: false,
      passwordInput: {
        placeholder: "Type your new password",
        label: "New password",
        model: "",
        inputType: "password",
        error: "",
        disabled: true
      },
      passwordInputChecked: false
    };
  },
  computed: {
    passwordHasSixChar() {
      return this.passwordInput.model.length >= 6;
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.passwordInput.model);
    },
    passwordHasLowescase() {
      return /[a-z]/.test(this.passwordInput.model);
    },
    passwordHasNumber() {
      return /\d/.test(this.passwordInput.model);
    },
    newPasswordIsVerified() {
      return (
        this.passwordHasSixChar &&
        this.passwordHasUppercase &&
        this.passwordHasLowescase &&
        this.passwordHasNumber
      );
    }
  },
  mounted() {
    this.verifiedTokenLoading = true;
    const params = this.$route.params;
    this.$http.plain
      .get(`/password_resets/${params.token}`)
      .then(({ status }) => {
        if (status !== 200) return;
        this.verifiedToken = true;
        this.passwordInput.disabled = false;
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Invalid token";
        this.showToast({ title: "", message, type: "error" });
      })
      .finally(() => {
        this.verifiedTokenLoading = false;
      });
  },
  methods: {
    ...mapActions(["showToast"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
      payload.input.error = "";
      this.passwordInputChecked = false;
    },
    savePassword() {
      this.passwordInputChecked = true;
      if (!this.newPasswordIsVerified) return;

      const params = this.$route.params;
      const password = this.passwordInput.model;
      this.$http.plain
        .patch(`/password_resets/${params.token}`, {
          password,
          password_confirmation: password
        })
        .then(({ status }) => {
          if (status !== 200) return;
          const title = "Your password has been updated";
          const message = "Now you can sign in with your new password.";
          this.showToast({ title, message, type: "success" });
          this.goSignin();
        })
        .catch(error => {
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.error) ||
            "Unable to update password";
          this.showToast({ title: "", message, type: "error" });
        });
    },
    goSignin() {
      return this.$router.push({ name: "Signin" });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/auth/ResetPassword.vue